import type { BadgeTone, HpStatusTone, HpTagTone } from './primitives.js';

/**
 * Status vocabulary of the Back Office (runs, jobs, clusters, matches, links…) mapped to the
 * design system's tones. Colour is never chosen by hand at a call site (design system §6):
 * green = done/positive, blue = in progress, amber = waiting on a human, purple = parked,
 * gray = neutral/terminal, danger/red = failed or refused.
 */
const STATUS_TONE: Record<string, HpTagTone> = {
  // runs and jobs
  queued: 'info',
  running: 'info',
  completed: 'success',
  failed: 'danger',
  paused: 'warning',
  cancelled: 'neutral',
  // clusters, pairs, review
  pending: 'warning',
  approved: 'success',
  declined: 'danger',
  merging: 'info',
  merged: 'success',
  deleted: 'neutral',
  stale: 'neutral',
  suppressed: 'neutral',
  // merge tasks
  verifying: 'info',
  patched: 'info',
  deleting: 'info',
  verified: 'success',
  skipped_stale: 'neutral',
  // enrichment matches
  auto_accepted: 'success',
  accepted: 'success',
  rejected: 'danger',
  no_match: 'neutral',
  // publish tasks
  planned: 'info',
  applied: 'success',
  skipped: 'warning',
  // self-service links
  active: 'success',
  used: 'neutral',
  revoked: 'danger',
  expired: 'warning',
  // website monitoring
  broken: 'danger',
  superseded: 'neutral',
  dismissed: 'neutral',
  // units
  available: 'success',
  reserved: 'warning',
  sold: 'danger',
};

/** Tone of an `HpTag` for a machine status; unknown statuses are neutral. */
export function statusTone(status: string | null | undefined): HpTagTone {
  return (status && STATUS_TONE[status]) || 'neutral';
}

/** Same vocabulary as a legacy `.badge` colour. */
export function statusBadgeTone(status: string | null | undefined): BadgeTone {
  switch (statusTone(status)) {
    case 'success':
      return 'green';
    case 'info':
      return 'blue';
    case 'warning':
      return 'amber';
    case 'danger':
      return 'purple';
    default:
      return 'gray';
  }
}

/** Compact `.hp-status` tone (no info tone exists there; in-progress reads as neutral). */
export function statusDotTone(status: string | null | undefined): HpStatusTone {
  const tone = statusTone(status);
  return tone === 'info' ? 'neutral' : tone;
}

/** Human label: snake_case machine status → words. */
export function statusLabel(status: string | null | undefined): string {
  return (status ?? '').replaceAll('_', ' ');
}
